import './Login/Login.scss';

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Auth } from '../apis/Auth';
import Background from '../components/AuthBackground/AuthBackground';
import { useQuery } from 'core/hooks';

const DEFAULT_VALUE = {
  password: '',
  confirmPassword: ''
};

const getPasswordChecks = (password: string) => [
  { label: 'At least 8 characters', met: password.length >= 8 },
  { label: 'At least 1 uppercase letter', met: /[A-Z]/.test(password) },
  { label: 'At least 1 lowercase letter', met: /[a-z]/.test(password) },
  { label: 'At least 1 digit', met: /\d/.test(password) },
  {
    label: 'At least 1 special character',
    met: /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(password)
  }
];

const getStrengthLabel = (score: number) => {
  const labels = ['', 'Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
  return labels[score];
};

const getStrengthColor = (score: number) => {
  const colors = ['', '#ff4d4d', '#ff8c00', '#ffc107', '#4caf50', '#2e7d32'];
  return colors[score];
};

const ResetPassword = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(DEFAULT_VALUE);
  const [errors, setErrors] = useState(DEFAULT_VALUE);
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [confirmPasswordVisibility, setConfirmPasswordVisibility] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const query = useQuery();
  const token = query.get('token');

  const passwordChecks = getPasswordChecks(formData.password);
  const strengthScore = passwordChecks.filter((c) => c.met).length;
  const allPasswordRulesMet = strengthScore === 5;

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const formErrors: any = {};
    let hasErrors = false;

    const { password, confirmPassword } = formData;

    if (!password.trim()) {
      formErrors.password = 'Password is required';
      hasErrors = true;
    } else if (!allPasswordRulesMet) {
      formErrors.password = 'Password does not meet all requirements';
      hasErrors = true;
    }

    if (!confirmPassword.trim()) {
      formErrors.confirmPassword = 'Confirm Password is required';
      hasErrors = true;
    }

    if (!hasErrors) {
      if (password.trim() !== confirmPassword.trim()) {
        formErrors.confirmPassword = 'Passwords do not match';
        hasErrors = true;
      }
    }

    if (hasErrors) {
      setErrors(formErrors);
      return;
    }

    const payload = {
      token: token,
      new_password: password
    };

    const data = await Auth.resetPassword(payload);
    if (data) {
      navigate('/login');
    }
  };

  const showChecklist = passwordFocused || formData.password.length > 0;
  const showStrength = formData.password.length > 0;
  const showMatchFeedback = formData.confirmPassword.length > 0;
  const passwordsMatch = formData.password === formData.confirmPassword;

  return (
    <div className="relative">
      <Background imageUrl="/auth/authback.png" />

      <div className="main-container h-screen">
        <div className="mt-[20px] align-center flex justify-center">
          <Link to="/login">
            <img width={200} src="/brand-logo/logo.svg" alt="" />
          </Link>
        </div>
        <div className="mt-[20px] align-center flex justify-center">
          <h3 className="AuthHeader Manrope text-white">Forgot Password</h3>
        </div>

        <div className="flex justify-center mt-4">
          <div className="AuthBody flex justify-center">
            <div className="w-[400px]">
              {/* form section */}
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <div className="flex flex-start">
                    <label
                      htmlFor="password"
                      className="Manrope text-white block text-sm font-semibold mb-1 InputLabel mt-[10px]"
                    >
                      Password:
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                      type={passwordVisibility ? 'text' : 'password'}
                      id="password"
                      name="password"
                      className="w-full transparent-input border mt-[5px]  text-white Input"
                    />
                    <img
                      onClick={() => {
                        setPasswordVisibility(!passwordVisibility);
                      }}
                      className="absolute eyeIcon pointer "
                      src="/auth/Icon.png"
                      alt=""
                    />
                  </div>
                  <div className="flex flex-start">
                    {errors.password && (
                      <div className="text-red-500 text-sm mt-1">{errors.password}</div>
                    )}
                  </div>

                  {showChecklist && (
                    <ul className="password-checklist">
                      {passwordChecks.map((check) => (
                        <li key={check.label} className={check.met ? 'met' : ''}>
                          <span className="check-icon">{check.met ? '\u2713' : '\u25CB'}</span>
                          {check.label}
                        </li>
                      ))}
                    </ul>
                  )}

                  {showStrength && (
                    <div className="strength-meter">
                      <div className="strength-bar-track">
                        <div
                          className="strength-bar-fill"
                          style={{
                            width: `${(strengthScore / 5) * 100}%`,
                            backgroundColor: getStrengthColor(strengthScore)
                          }}
                        />
                      </div>
                      <span
                        className="strength-label"
                        style={{ color: getStrengthColor(strengthScore) }}
                      >
                        {getStrengthLabel(strengthScore)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <div className="flex flex-start">
                    <label
                      htmlFor="confirmPassword"
                      className="Manrope text-white block text-sm font-semibold mb-1 InputLabel mt-[10px]"
                    >
                      Confirm Password:
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      type={confirmPasswordVisibility ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      className="w-full transparent-input border mt-[5px]  text-white Input"
                    />
                    <img
                      onClick={() => {
                        setConfirmPasswordVisibility(!confirmPasswordVisibility);
                      }}
                      className="absolute eyeIcon pointer "
                      src="/auth/Icon.png"
                      alt=""
                    />
                  </div>
                  <div className="flex flex-start">
                    {errors.confirmPassword && (
                      <div className="text-red-500 text-sm mt-1">{errors.confirmPassword}</div>
                    )}
                  </div>
                  {showMatchFeedback && (
                    <div
                      className={`password-match-feedback ${passwordsMatch ? 'match' : 'no-match'}`}
                    >
                      {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                    </div>
                  )}
                </div>
                <button type="submit" className="submitBtn w-full mt-8">
                  Reset Password
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
